import React from 'react';
import {
    generateCreateEntity,
    generateLoadList,
    generateLoadEntity,
    generateDeleteEntity,
} from '../utils/mobx';
import services from '../services';
import { makeAutoObservable, runInAction } from 'mobx';
import SemiCircleProgress from '../components/manager/assessment/semi-circle-progress';
import EmployeeRemindersModal from '../containers/assessment/AssessmentView/reminders/employee-modal';
import ListRemindersModal from '../containers/assessment/AssessmentView/reminders/list-modal';
import {
    CheckboxInput,
    TextDefault,
    DarkArrowTooltipIcon,
} from '@awarego-ui/components';
import BounceReasonCell from '../containers/users/components/BounceReasonCell';
import NameAndSublistCell from '../components/manager/NameAndSublistCell';
import { observer } from 'mobx-react';
import moment from 'moment';
import omit from 'lodash/omit';
import SelectionController from './SelectionController';
import isEqual from 'lodash/isEqual';
import debounceFn from 'debounce-fn';
import {
    expandUserEmail,
    expandParticipant,
    expandUserName,
    extractErrorMessage,
} from '../utils/helpers';
import { USER_ROLE } from '../utils/constants';
import { caseAndDiacriticsInsensitiveSort } from '../components/table/table-helpers';
import { ASSESSMENT_TYPES } from '../utils/constants';

CheckboxInput.propTypes = {};

class AssessmentEmployeesStore {
    usersList = [];
    loadingUsersList = null;
    addingUsersToList = null;
    initiallyLoaded = false;
    isDownloading = false;
    error = null;
    queryContext = null;
    selectedListsInFilter = [];
    usersTotal = 0;
    listType = null;
    // Bounced dialog: "show obsolete" toggle + obsolete count returned by the API.
    showObsolete = false;
    bouncedObsoleteCount = 0;
    employeesToBeSentReminders = 0;
    loadingEmployeesToBeSentReminders = false;
    remindersSentRecently = [];
    detailsMenu = null;
    loadingSigninLink = false;
    lastSigninLinkUrl = null;

    constructor(assessmentStore, commonStore, mainStore) {
        makeAutoObservable(this);
        this.assessmentStore = assessmentStore;
        this.commonStore = commonStore;
        this.mainStore = mainStore;
        this.selection = new SelectionController(
            this.usersList,
            this,
            (x) => x.email
        );

        this.assessmentStore.editingAssessment &&
            this.toggleAllListsSelected(
                this.assessmentStore.editingAssessment.selectedUserListsIds
            );
        this.setMinScoreDebounced = debounceFn(this.setMinScore, { wait: 500 });
        this.setMaxScoreDebounced = debounceFn(this.setMaxScore, { wait: 500 });
    }

    setError(error, type) {
        switch (type) {
            default:
                this.error = error;
                break;
        }
    }

    setUserListSearchValue(value) {
        if (this.queryContext) this.queryContext.filter = value;
    }

    get userListSearchValue() {
        return (this.queryContext && this.queryContext.filter) || '';
    }

    get userListsSelectedDescription() {
        if (!this.assessmentStore.editingAssessment) return '';
        const selectedLists =
            this.assessmentStore.editingAssessment.userListsSelected.filter(
                (list) => this.selectedListsInFilter.includes(list.id)
            );

        if (selectedLists.length === 1) return selectedLists[0].name;
        if (
            this.selectedListsInFilter.length ===
            this.assessmentStore.editingAssessment.userListsSelected.length
        )
            return `All lists (${this.assessmentStore.editingAssessment.userListsSelected.length})`;

        return `${this.selectedListsInFilter.length} lists`;
    }

    setListId(value) {
        if (this.queryContext) this.queryContext.listId = value;
    }

    setMinScore(value) {
        if (this.queryContext) this.queryContext.minScore = value;
    }

    setMaxScore(value) {
        if (this.queryContext) this.queryContext.maxScore = value;
    }

    setScores(minScore, maxScore) {
        this.setMinScore(minScore);
        this.setMaxScore(maxScore);
    }

    setPagingParams(pageIndex, pageSize) {
        if (this.queryContext) {
            this.queryContext.p = pageIndex;
            this.queryContext.n = pageSize;
        }
    }

    setScoreName(value) {
        if (this.queryContext) this.queryContext.scoreName = value;
    }

    setQueryContext(context = {}) {
        this.queryContext = context;
    }

    setSortBy(sortBy) {
        if (this.queryContext && sortBy && sortBy.length > 0) {
            this.queryContext.order = `${sortBy[0].id} ${
                sortBy[0].desc ? 'desc' : 'asc'
            }`;
        }
    }

    setThreatArea(value) {
        if (this.queryContext) this.queryContext.threatArea = value;
    }

    async showRemindersModal(
        useSelection,
        type,
        item,
        itemName,
        reloadAfterSending = false
    ) {
        // there are five cases how this can be used
        // 1. remind all users
        // 2. remind one list exactly
        // 3. remind selected lists
        // 4. remind selected users
        // 5. remind one user exactly
        this.handleMenuClose();

        let userLists;
        if (
            type === 'list' &&
            (Array.isArray(item) || Number.isInteger(item))
        ) {
            userLists = Array.isArray(item) ? item : [item];
        }
        await this.loadEmployeesToBeSentReminders(
            useSelection,
            userLists ? { userLists: userLists } : {}
        );

        if (!this.loadingEmployeesToBeSentReminders) {
            if (this.employeesToBeSentReminders === 0) {
                this.commonStore.showConfirm(
                    'There are no employees to be reminded.',
                    'Close',
                    'Send reminders',
                    null,
                    false,
                    false
                );
            } else {
                if (type === 'user') {
                    this.commonStore.showConfirm(
                        <EmployeeRemindersModal
                            employeeCount={
                                useSelection
                                    ? this.employeesToBeSentReminders
                                    : 1
                            }
                            employeeName={itemName}
                        />,
                        'Send reminders',
                        'Send reminders',
                        async () => {
                            await this.sendReminders(
                                useSelection,
                                item ? { emails: [item] } : {}
                            );
                        }
                    );
                } else {
                    this.commonStore.showConfirm(
                        <ListRemindersModal
                            employeesToBeSentReminders={
                                this.employeesToBeSentReminders
                            }
                            remindersSentRecently={this.remindersSentRecently}
                            listName={itemName}
                            lists={userLists}
                        />,
                        'Send reminders',
                        'Send reminders',
                        async () => {
                            await this.sendReminders(
                                useSelection,
                                userLists ? { userLists: userLists } : {},
                                reloadAfterSending
                            );
                        }
                    );
                }
            }
        }
    }

    async sendReminders(useSelection, body, reloadAfterSending) {
        try {
            const companyId = this.mainStore.currentCompany.company_id;
            const assessmentId = this.assessmentStore.editingAssessment.id;
            let params;
            if (useSelection) {
                params = {
                    ...omit(this.queryContext, ['scoreName', 'subheading']),
                    ...this.selection.selectionContext,
                };
            }
            const result = await services.Companies.assessmentsService(
                companyId
            ).sendReminders(assessmentId, params, {
                ...body,
                reminderOption: 2,
            });
            if (result) {
                if (result.success) {
                    this.commonStore.success('Reminders sent!');
                } else {
                    this.commonStore.warn(
                        result.reason
                            ? result.reason
                            : 'Reminders were not sent!'
                    );
                }
            }
            if (result && result.success && reloadAfterSending)
                this.assessmentStore.loadRemindersOverview(
                    companyId,
                    assessmentId
                );
        } catch (e) {
            runInAction(() => {
                this.error = extractErrorMessage(e);
            });
        } finally {
            runInAction(() => {
                this.selection.clearSelection();
            });
        }
    }

    get pagingParams() {
        if (this.queryContext) {
            return {
                pageIndex: this.queryContext.p,
                pageSize: this.queryContext.n,
            };
        }
        return {};
    }

    get listId() {
        return this.queryContext && this.queryContext.listId;
    }

    get listIdHash() {
        return (
            this.queryContext &&
            this.queryContext.listId &&
            this.queryContext.listId.join('-')
        );
    }

    get minScore() {
        return this.queryContext && this.queryContext.minScore
            ? this.queryContext.minScore
            : 0;
    }

    get maxScore() {
        return this.queryContext && this.queryContext.maxScore
            ? this.queryContext.maxScore
            : 100;
    }

    get subheading() {
        return this.queryContext ? this.queryContext.subheading : '';
    }

    get scoreName() {
        return this.queryContext && (this.queryContext.scoreName || '');
    }

    get pageIndex() {
        return this.queryContext && this.queryContext.p;
    }

    get pageSize() {
        return this.queryContext && this.queryContext.n;
    }

    get order() {
        return this.queryContext && this.queryContext.order;
    }

    toggleListSelected = (listId) => {
        if (this.selectedListsInFilter.includes(listId))
            this.selectedListsInFilter.remove(listId);
        else this.selectedListsInFilter.push(listId);
        this.setListId(this.selectedListsInFilter);
    };

    toggleAllListsSelected = (array) => {
        if (
            isEqual(
                array.slice().sort((a, b) => a - b),
                this.selectedListsInFilter.slice().sort((a, b) => a - b)
            )
        ) {
            this.clearSelectedListsInFilter();
        } else {
            this.selectedListsInFilter.replace(array);
        }
        if (this.listId !== null) this.setListId(null);
    };

    setShowObsolete(value) {
        this.showObsolete = value;
    }

    clearSelectedListsInFilter = () => {
        this.selectedListsInFilter.replace([]);
    };

    async downloadCSV(companyId, assessmentId) {
        if (this.isDownloading) return true;
        this.isDownloading = true;

        try {
            const options = {
                responseType: 'blob',
            };

            let response;

            switch (this.listType) {
                case 'bounced':
                    response = await services.Companies.assessmentsService(
                        companyId
                    )
                        .bouncedEmailsService(assessmentId)
                        .list(
                            {
                                csv: true,
                                listIds: this.queryContext.listId,
                                obsolete: this.showObsolete,
                            },
                            options
                        );
                    break;
                default:
                    response = await services.Companies.assessmentsService(
                        companyId
                    )
                        .usersService(assessmentId)
                        .list(
                            {
                                csv: true,
                                ...omit(this.queryContext, [
                                    'scoreName',
                                    'subheading',
                                ]),
                                ...this.selection.selectionContext,
                            },
                            options
                        );
                    break;
            }
            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: response.headers['content-type'],
                })
            );

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } finally {
            this.isDownloading = false;
            this.selection.clearSelection();
        }
        //  }, 5000);
    }

    get hiddenColumns() {
        let temp = [];
        if (
            this.assessmentStore.isAssessmentInsight ||
            (this.assessmentStore.editingAssessment &&
            this.assessmentStore.editingAssessment.isScorm)
        )
            temp.push('listName');
        if(this.assessmentStore.isAssessmentInsight){
            temp.push('more_actions');
            temp.push('name')
            temp.push('email')
        }
        else{
            temp.push('participant');
        }

        return temp;
    }

    handleMenuOpen(e, employee) {
        this.detailsMenu = { employee, anchorEl: e.currentTarget };
        e.stopPropagation();
    }

    handleMenuClose(e) {
        this.detailsMenu = null;
        if (e) e.stopPropagation();
    }

    loadSigninLink = generateLoadEntity(
        'loadSigninLink',
        this,
        'loadingSigninLink',
        async function (companyId, assessmentId, email) {
            return await services.Companies.assessmentsService(
                companyId
            ).getSigninLink(assessmentId, email);
        },
        'lastSigninLinkUrl',
        'url',
        undefined,
        { clearResultBeforeLoad: true }
    );

    moreActionsMenu(employee) {
        return !this.mainStore.isAdmin ? null : (
            <DarkArrowTooltipIcon
                customFunction={(e) => this.handleMenuOpen(e, employee)}
                row={employee}
                title="More"
                icon={'more'}
            ></DarkArrowTooltipIcon>
        );
    }

    get columns() {
        switch (this.listType) {
            case 'bounced':
                return [
                    {
                        Header: 'Name',
                        id: 'name',
                        accessor: (x) => {
                            return x.name || x.email;
                        },
                        Cell: (x) => <TextDefault bold>{x.value}</TextDefault>,
                        sortType: caseAndDiacriticsInsensitiveSort,
                    },
                    {
                        Header: 'List',
                        accessor: 'lists_names',
                        Cell: (x) => {
                            if (x.value && x.value.length > 0) {
                                return (
                                    <NameAndSublistCell
                                        subListItems={x.value}
                                        maxVisibleSubListCount={1}
                                    ></NameAndSublistCell>
                                );
                            } else {
                                return (
                                    <TextDefault lighter>
                                        List unavailable
                                    </TextDefault>
                                );
                            }
                        },
                    },
                    {
                        Header: 'Email',
                        accessor: 'email',
                    },
                    {
                        Header: 'Reason',
                        accessor: 'reason',
                        disableSortBy: true,
                        Cell: (x) => <BounceReasonCell reason={x.value} />,
                    },
                ];
            default:
                return [
                    {
                        Header: 'Participant',
                        id: 'participant',
                        accessor: (x) => expandParticipant(x.email),
                        sortType: caseAndDiacriticsInsensitiveSort,
                        Cell: observer((x) => {
                            return (
                                <CheckboxInput
                                    label={x.value}
                                    checked={
                                        this.selection.effectiveSelectionOnCurrentPage[
                                            x.row.original.email
                                            ]
                                    }
                                    noYPadding={true}
                                    onChange={(e) => {
                                        this.selection.toggleRow(
                                            x.row.original,
                                            e.target.checked
                                        );
                                    }}
                                />
                            );
                        }),
                    },
                    {
                        Header: 'Name',
                        id: 'name',
                        accessor: (x) => {
                            // scorm name  is formated as "id|[name]|[email]"
                            // so check if its  scorm name and parse
                            return expandUserName(x.name || x.email);
                        },
                        sortType: caseAndDiacriticsInsensitiveSort,
                        Cell: observer((x) => {
                            return (
                                <CheckboxInput
                                    label={x.value}
                                    checked={
                                        this.selection
                                            .effectiveSelectionOnCurrentPage[
                                            x.row.original.email
                                        ]
                                    }
                                    noYPadding={true} //Removes Y padding and margin, making it fit better into table row
                                    onChange={(e) => {
                                        this.selection.toggleRow(
                                            x.row.original,
                                            e.target.checked
                                        );
                                    }}
                                />
                            );
                        }),
                    },
                    {
                        Header: 'Completed date',
                        accessor: 'completedDate',
                        Cell: (x) =>
                            x.value
                                ? moment(x.value).format('MMM DD[, ]YYYY')
                                : '-',
                    },
                    {
                        Header: 'List',
                        accessor: 'listName',
                        Cell: (x) =>
                            x.value ? (
                                x.value.replace(/,(?=[^\s])/g, ', ')
                            ) : (
                                <TextDefault lighter>
                                    List unavailable
                                </TextDefault>
                            ),
                    },
                    {
                        Header: 'Email',
                        id: 'email',
                        accessor: (x) => {
                            // scorm name  is formated as "id|[name]|[email]"
                            // so check if its  scorm name and parse

                            return expandUserEmail(x.name, x.email);
                        },
                    },
                    {
                        Header: this.scoreName,
                        accessor: 'scorePercent',
                        Cell: (x) => (
                            <SemiCircleProgress
                                percentage={x.value}
                                width={48}
                                top={12}
                                halfCircle
                                percentageText
                                showNA={!x.row.original.started}
                            />
                        ),
                    },
                    ...(this.mainStore.currentRole === USER_ROLE.ADMIN
                        ? [
                              {
                                  Header: ' ',
                                  id: 'more_actions',
                                  Cell: (x) =>
                                      this.moreActionsMenu(x.row.original),
                              },
                          ]
                        : []),
                ];
        }
    }

    restartAssessment = generateDeleteEntity(
        'restartAssessment',
        this,
        'restartingAssessment',
        (email) => {
            return services.Companies.assessmentsService(
                this.mainStore.currentCompany.company_id
            ).restartAssessment(
                this.assessmentStore.editingAssessment.id,
                email
            );
        }
    );

    loadEmployeesToBeSentReminders = generateLoadEntity(
        'employeesToBeSentReminders',
        this,
        'loadingEmployeesToBeSentReminders',
        async (useSelection, body = {}) => {
            const companyId = this.mainStore.currentCompany.company_id;
            const assessmentId = this.assessmentStore.editingAssessment.id;
            let params;
            if (useSelection) {
                params = {
                    ...omit(this.queryContext, ['scoreName', 'subheading']),
                    ...this.selection.selectionContext,
                };
            }
            const result = await services.Companies.assessmentsService(
                companyId
            ).countReminders(assessmentId, params, {
                ...body,
                reminderOption: 2,
            });
            runInAction(() => {
                this.remindersSentRecently.replace(result.lastReminders);
            });
            return result.remindersCount;
        },
        'employeesToBeSentReminders'
    );

    loadBouncedEmails = generateLoadList(
        'usersList',
        this,
        'loadingUsersList',
        async (companyId, assessmentId) => {
            const result = await services.Companies.assessmentsService(
                companyId
            )
                .bouncedEmailsService(assessmentId)
                .list({
                    listIds: this.queryContext.listId,
                    obsolete: this.showObsolete,
                });
            // Tolerate both the new ({ users, obsoleteCount }) and the legacy
            // (array, possibly of email strings) response shapes during
            // independent UI/API rollout.
            const rawUsers = Array.isArray(result)
                ? result
                : result.users || [];
            runInAction(() => {
                this.bouncedObsoleteCount = Array.isArray(result)
                    ? 0
                    : result.obsoleteCount || 0;
            });
            return rawUsers.map((x) =>
                typeof x === 'string' ? { email: x } : x
            );
        },
        'usersList'
    );

    loadUsers = generateLoadList(
        'usersList',
        this,
        'loadingUsersList',
        async (companyId, assessmentId) => {
            const result = await services.Companies.assessmentsService(
                companyId
            )
                .usersService(assessmentId)
                .list({
                    ...omit(this.queryContext, ['scoreName', 'subheading']),
                });
            runInAction(() => {
                this.usersTotal = result.count;
                this.selection.usersTotal = result.count;
            });
            return result.users;
        },
        'usersList'
    );

    addUsersToList = generateCreateEntity(
        'addUsersToList',
        this,
        'addingUsersToList',
        async (companyId, assessmentId, targetListId, targetListName) => {
            const params = {
                ...omit(this.queryContext, ['scoreName', 'subheading']),
                ...this.selection.selectionContext,
            };
            if (targetListId) params.targetListId = targetListId;
            if (targetListName) params.targetListName = targetListName;
            return await services.Companies.assessmentsService(companyId)
                .usersService(assessmentId)
                .addUsersToList(params);
        }
    );

    debouncedLoadUsers = debounceFn(this.loadUsers, { wait: 500 });
    debouncedLoadBouncedEmails = debounceFn(this.loadBouncedEmails, {
        wait: 400,
    });

    async loadEmployeesList(companyId, assessmentId) {
        try {
            switch (this.listType) {
                case 'bounced':
                    // First open loads immediately; later filter changes
                    // (lists / show-obsolete) are debounced.
                    return this.initiallyLoaded
                        ? this.debouncedLoadBouncedEmails(
                              companyId,
                              assessmentId
                          )
                        : this.loadBouncedEmails(companyId, assessmentId);
                default:
                    return this.loadUsers(companyId, assessmentId);
            }
        } finally {
            this.initiallyLoaded = true;
        }
    }

    deleteEmployeeFromScormAssessment = generateDeleteEntity(
        'deleteEmployeeFromScormAssessment',
        this,
        'deletingEmployeeFromScormAssessment',
        async (companyId, sessionId) => {
            await services.Companies.cmi5Service(
                companyId
            ).deleteEmployeeFromScormAssessment(sessionId);
            runInAction(() => {
                this.usersList = this.usersList.filter(
                    (x) => x.email != sessionId
                );
                this.usersTotal = this.usersList.length;
            });
        }
    );
}

class AssessmentEmployeesStoresFactory {
    employeesStores = {};

    constructor(assessmentStore, commonStore, mainStore) {
        makeAutoObservable(this);
        this.assessmentStore = assessmentStore;
        this.commonStore = commonStore;
        this.mainStore = mainStore;
    }

    get modalEmployeesStore() {
        if (!this.assessmentStore) return null;
        if (!this.employeesStores['modal'])
            this.employeesStores['modal'] = new AssessmentEmployeesStore(
                this.assessmentStore,
                this.commonStore,
                this.mainStore
            );

        return this.employeesStores['modal'];
    }

    get employeesOverviewStore() {
        if (!this.assessmentStore) return null;
        if (!this.employeesStores['overview']) {
            this.employeesStores['overview'] = new AssessmentEmployeesStore(
                this.assessmentStore,
                this.commonStore,
                this.mainStore
            );
            this.employeesStores['overview'].listType = 'users';
            this.employeesStores['overview'].queryContext = {};
            this.employeesStores['overview'].setScoreName('Threat area score');
        }

        return this.employeesStores['overview'];
    }

    hideEmployeesModal() {
        // just recreate instead cleanup props
        this.employeesStores['modal'] = new AssessmentEmployeesStore(
            this.assessmentStore,
            this.commonStore,
            this.mainStore
        );
    }

    showEmployeesModal(type, context = {}) {
        this.modalEmployeesStore.listType = type;
        this.modalEmployeesStore.queryContext = context;
        this.modalEmployeesStore.showObsolete = false;
        this.modalEmployeesStore.bouncedObsoleteCount = 0;
        this.modalEmployeesStore.initiallyLoaded = false;
    }
}

export default AssessmentEmployeesStoresFactory;
